import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Truck, ShoppingCart, TShirt, Phone, ArrowRight, 
  CheckCircle, Shield, Clock, MapPin, Star, CaretRight,
  FacebookLogo, InstagramLogo, Plus, Wrench, Car, MagnifyingGlass,
  NavigationArrow, CalendarBlank, Users, Lightning
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { ContactFormModal } from '../components/ContactFormModal';
import PWAInstallBanner from '../components/PWAInstallBanner';
import { trackPageView, trackEvent, trackConversion, Events, EventCategories, ConversionTypes } from '../services/analytics';

// Social Media Links - Easy to manage
const SOCIAL_LINKS = [
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/share/1Cyoii6aCm/',
    icon: FacebookLogo,
    color: '#1877F2'
  },
  // Add more social links here as needed:
  // { name: 'Instagram', url: 'https://instagram.com/yourhandle', icon: InstagramLogo, color: '#E4405F' },
  // { name: 'Twitter', url: 'https://twitter.com/yourhandle', icon: TwitterLogo, color: '#1DA1F2' },
];

// Zip code distance database (approximate miles from Tarboro 27886)
const ZIP_CODE_DISTANCES = {
  '27886': { city: 'Tarboro', miles: 0 },
  '27801': { city: 'Rocky Mount', miles: 12 },
  '27802': { city: 'Rocky Mount', miles: 12 },
  '27803': { city: 'Rocky Mount', miles: 14 },
  '27804': { city: 'Rocky Mount', miles: 11 },
  '27893': { city: 'Wilson', miles: 22 },
  '27894': { city: 'Wilson', miles: 24 },
  '27896': { city: 'Wilson', miles: 20 },
  '27858': { city: 'Greenville', miles: 25 },
  '27834': { city: 'Greenville', miles: 28 },
  '27833': { city: 'Greenville', miles: 27 },
  '27856': { city: 'Nashville', miles: 18 },
  '27809': { city: 'Battleboro', miles: 8 },
  '27863': { city: 'Pinetops', miles: 15 },
  '27862': { city: 'Pinetops', miles: 14 },
  '27828': { city: 'Farmville', miles: 30 },
  '27829': { city: 'Farmville', miles: 32 },
  '27874': { city: 'Scotland Neck', miles: 20 },
  '27839': { city: 'Halifax', miles: 28 },
  '27843': { city: 'Halifax', miles: 30 },
  '27823': { city: 'Enfield', miles: 22 },
  '27871': { city: 'Robersonville', miles: 25 },
  '27822': { city: 'Elm City', miles: 15 },
  '27878': { city: 'Sharpsburg', miles: 10 },
  '27882': { city: 'Spring Hope', miles: 20 },
  '27891': { city: 'Whitakers', miles: 12 },
  '27816': { city: 'Castalia', miles: 16 },
  '27860': { city: 'Pantego', miles: 38 },
  '27857': { city: 'Oak City', miles: 22 },
  '27847': { city: 'Lewiston Woodville', miles: 35 },
  '27864': { city: 'Princeville', miles: 2 },
  '27879': { city: 'Simpson', miles: 28 },
  '27851': { city: 'Lucama', miles: 25 },
};

const Landing = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [showChat, setShowChat] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [zipResult, setZipResult] = useState(null);

  // Track page view on mount
  useEffect(() => {
    trackPageView('Homepage', '/');
  }, []);

  const checkZipCode = (zip) => {
    setZipCode(zip);
    if (zip.length === 5) {
      const info = ZIP_CODE_DISTANCES[zip];
      if (info) {
        const extraMiles = Math.max(0, info.miles - 15);
        const extraCost = extraMiles * 1.5;
        setZipResult({
          found: true,
          inArea: info.miles <= 40,
          city: info.city,
          miles: info.miles,
          extraMiles,
          extraCost,
          isFree: info.miles <= 15
        });
      } else {
        const isNCZip = zip.startsWith('27') || zip.startsWith('28');
        setZipResult({
          found: false,
          isNCZip,
          message: isNCZip 
            ? "Contact us to confirm coverage for your area."
            : "We serve Tarboro, NC and surrounding areas within 40 miles."
        });
      }
    } else {
      setZipResult(null);
    }
  };

  const getDashboardLink = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'owner') return '/owner/dashboard';
    if (user?.role === 'worker') return '/worker/dashboard';
    return '/customer/dashboard';
  };

  // NC HELP HUB - Brand Positioning: Rides as PRIMARY, then Everyday Services
  // PRIMARY SERVICE
  const primaryService = {
    id: 'rides',
    title: 'NC Help Hub Rides',
    description: 'Personal transportation when you need it. Local rides, appointments, airport transfers.',
    icon: Car,
    color: '#8B5CF6',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&q=80',
    features: ['Local Rides', 'Medical Appointments', 'Airport Transfers', 'Scheduled Pickups'],
    link: '/book-ride',
    isPrimary: true
  };

  // EVERYDAY SERVICES (Ordered: Courier, Errands, Laundry - NO CLEANING)
  const everydayServices = [
    {
      id: 'courier',
      title: 'Courier & Delivery',
      description: 'Fast and reliable local package delivery',
      icon: Truck,
      color: '#3B82F6',
      image: 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=600&q=80',
      features: ['Package Delivery', 'Documents', 'Business Deliveries', 'Same-Day'],
      link: '/book-courier'
    },
    {
      id: 'errands',
      title: 'Errands & Tasks',
      description: 'Grocery pickup, prescriptions, store returns, and more',
      icon: ShoppingCart,
      color: '#EC4899',
      image: 'https://images.pexels.com/photos/6868791/pexels-photo-6868791.jpeg?auto=compress&cs=tinysrgb&w=600',
      features: ['Grocery Pickup', 'Prescriptions', 'Store Returns', 'General Tasks'],
      link: '/book-errand'
    },
    {
      id: 'laundry',
      title: 'Laundry Pickup',
      description: 'Wash & fold with pickup and delivery (optional)',
      icon: TShirt,
      color: '#F4C2C2',
      image: 'https://images.pexels.com/photos/28576623/pexels-photo-28576623.jpeg?auto=compress&cs=tinysrgb&w=600',
      features: ['Wash & Fold', 'Pickup & Delivery', 'Same-Day Available'],
      link: '/book-laundry',
      isOptional: true
    }
  ];

  // All services for display (Rides first, then everyday services)
  const services = [primaryService, ...everydayServices];
  
  // Quick action buttons for hero section (Rides primary, then Courier, Errands, optional Laundry)
  const quickActions = [
    { id: 'rides', label: 'Book a Ride', icon: Car, color: '#8B5CF6', link: '/book-ride', isPrimary: true },
    { id: 'courier', label: 'Send Package', icon: Truck, color: '#3B82F6', link: '/book-courier' },
    { id: 'errands', label: 'Run Errand', icon: ShoppingCart, color: '#EC4899', link: '/book-errand' },
    { id: 'laundry', label: 'Laundry Pickup', icon: TShirt, color: '#F4C2C2', link: '/book-laundry' }
  ];

  const benefits = [
    { icon: Shield, title: 'Vetted Professionals', description: 'Background-checked workers' },
    { icon: Clock, title: 'Flexible Scheduling', description: 'Book on your time' },
    { icon: MapPin, title: 'GPS Tracking', description: 'Real-time updates' },
    { icon: Star, title: 'Quality Guaranteed', description: '5-star rated service' }
  ];

  const stats = [
    { value: 'Local', label: 'Tarboro Owned' },
    { value: '40mi', label: 'Service Radius' },
    { value: 'Fast', label: 'Same-Day Available' },
    { value: '24/7', label: 'Booking Online' }
  ];

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 sticky-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="nav-logo">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#D4AF37] to-[#E6C965] rounded-xl flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
              <span className="text-black font-bold text-base sm:text-lg">H</span>
            </div>
            <span className="text-lg sm:text-2xl font-bold text-white font-['Plus_Jakarta_Sans'] whitespace-nowrap">NC Help Hub</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            <a href="#services" className="text-slate-400 hover:text-[#D4AF37] transition-colors font-medium">Services</a>
            <Link to="/book-ride" className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 transition-colors font-semibold">
              <Car weight="bold" className="w-4 h-4" />
              Rides
              <span className="ml-1 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded border border-emerald-500/30">PRIMARY</span>
            </Link>
            <Link to="/track" className="text-slate-400 hover:text-[#D4AF37] transition-colors font-medium">Track Order</Link>
            <a href="#how-it-works" className="text-slate-400 hover:text-[#D4AF37] transition-colors font-medium">How It Works</a>
            <Link to="/join" className="text-[#F4C2C2] hover:text-[#D4AF37] transition-colors font-medium">Work With Us</Link>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <Link to={getDashboardLink()}>
                <Button className="bg-gradient-to-r from-[#D4AF37] to-[#E6C965] hover:from-[#E6C965] hover:to-[#D4AF37] text-black font-semibold px-4 sm:px-6 py-2 text-sm sm:text-base rounded-full shadow-lg shadow-[#D4AF37]/20" data-testid="dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" className="text-slate-300 hover:text-[#D4AF37] font-medium" data-testid="login-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/login" className="sm:hidden">
                  <Button variant="ghost" className="text-slate-300 hover:text-[#D4AF37] font-medium text-sm px-2" data-testid="login-btn-mobile">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#D4AF37] hover:bg-[#E6C965] text-black font-semibold px-4 sm:px-6 py-2 text-sm sm:text-base rounded-full" data-testid="get-started-btn">
                    <span className="hidden sm:inline">Get Started</span>
                    <span className="sm:hidden">Start</span>
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-emerald-500/10 rounded-full blur-[80px] sm:blur-[150px] animate-pulse" />
          <div className="absolute bottom-20 right-1/4 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#D4AF37]/8 rounded-full blur-[60px] sm:blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[180px]" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-10 sm:mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Primary Service Badge */}
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-semibold mb-6">
                <Car weight="fill" className="w-4 h-4" />
                Rides — Our Primary Service
                <Lightning weight="fill" className="w-4 h-4" />
              </span>
              
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-4 sm:mb-6 font-['Plus_Jakarta_Sans'] tracking-tight">
                <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">Your Local Ride.</span>
                <br />
                <span className="bg-gradient-to-r from-[#D4AF37] to-[#E6C965] bg-clip-text text-transparent">Your Local Delivery.</span>
                <br />
                <span className="text-slate-200 text-2xl sm:text-3xl md:text-4xl font-medium">One Easy Booking.</span>
              </h1>
              
              <p className="text-base sm:text-xl text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2">
                Book local rides, send packages, and get everyday errands handled through NC Help Hub. 
                Schedule a service or request an available provider when you need one.
              </p>
              
              {/* Primary Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4 sm:px-0 mb-8">
                <Link to="/book-ride" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-full font-semibold shadow-lg shadow-emerald-500/30 glow-rides" data-testid="hero-book-ride-btn">
                    <Car className="mr-2 w-5 h-5" weight="bold" />
                    Book a Ride
                    <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
                  </Button>
                </Link>
                <Link to="/book-courier" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-[#D4AF37] to-[#E6C965] hover:from-[#E6C965] hover:to-[#D4AF37] text-black text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-full font-semibold shadow-lg shadow-[#D4AF37]/20" data-testid="hero-book-delivery-btn">
                    <Truck className="mr-2 w-5 h-5" weight="bold" />
                    Book a Delivery
                    <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
                  </Button>
                </Link>
              </div>
              
              {/* Additional Service Links */}
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <Link to="/book-errand" className="text-slate-500 hover:text-pink-400 transition-colors flex items-center gap-1">
                  <ShoppingCart className="w-4 h-4" weight="bold" />
                  Run an Errand
                </Link>
                <span className="text-slate-700">•</span>
                <Link to="/book-laundry" className="text-slate-500 hover:text-purple-400 transition-colors flex items-center gap-1">
                  <TShirt className="w-4 h-4" weight="bold" />
                  Laundry Pickup
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Quick Action Buttons - 5 services (Rides primary + 4 everyday) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 max-w-5xl mx-auto mb-12"
          >
            {quickActions.map((action, idx) => (
              <Link 
                key={action.id} 
                to={action.link}
                data-testid={`quick-action-${action.id}`}
                className="group"
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.2 + idx * 0.05 }}
                  className="bg-[#111111] hover:bg-[#1a1a1a] border border-white/10 hover:border-white/20 rounded-2xl p-4 sm:p-6 text-center transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-lg"
                  style={{ '--action-color': action.color }}
                >
                  <div 
                    className="w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${action.color}20` }}
                  >
                    <action.icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: action.color }} weight="duotone" />
                  </div>
                  <span className="text-white text-sm sm:text-base font-semibold">{action.label}</span>
                </motion.div>
              </Link>
            ))}
          </motion.div>

          {/* Service Cards - Horizontal scroll on mobile */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex overflow-x-auto pb-4 gap-4 snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 md:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {services.map((service, idx) => (
              <Link 
                to={service.link || `/services?category=${service.id}`} 
                key={service.id} 
                data-testid={`service-card-${service.id}`} 
                className={`flex-shrink-0 snap-start ${service.isFeatured ? 'w-[320px] md:w-auto md:col-span-1 md:row-span-1' : 'w-[280px] md:w-auto'}`}
                onClick={() => {
                  trackEvent(Events.SERVICE_SELECTED, EventCategories.BOOKING, { 
                    service_id: service.id, 
                    service_title: service.title 
                  });
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
                  className={`group relative bg-[#111111] rounded-2xl overflow-hidden border transition-all duration-300 h-full ${
                    service.isFeatured 
                      ? 'border-[#8B5CF6]/50 hover:border-[#8B5CF6] ring-2 ring-[#8B5CF6]/20' 
                      : 'border-white/10 hover:border-[#D4AF37]/50'
                  }`}
                >
                  {/* NEW Badge for Featured Service */}
                  {service.isNew && (
                    <div className="absolute top-4 right-4 z-20">
                      <span className="px-3 py-1 bg-[#8B5CF6] text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
                        NEW
                      </span>
                    </div>
                  )}
                  
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/50 to-transparent" />
                    <div 
                      className="absolute top-4 left-4 w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${service.color}20` }}
                    >
                      <service.icon className="w-6 h-6" style={{ color: service.color }} weight="duotone" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className={`text-xl font-bold mb-2 font-['Cormorant_Garamond'] transition-colors ${
                      service.isFeatured 
                        ? 'text-[#A78BFA] group-hover:text-[#8B5CF6]' 
                        : 'text-white group-hover:text-[#D4AF37]'
                    }`}>
                      {service.title}
                    </h3>
                    <p className="text-white/50 text-sm mb-4">{service.description}</p>
                    <div className={`flex items-center text-sm font-medium ${
                      service.isFeatured ? 'text-[#8B5CF6]' : 'text-[#D4AF37]'
                    }`}>
                      {service.isFeatured ? 'Book a Ride' : 'Book Now'} <CaretRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" weight="bold" />
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 border-y border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#D4AF37] to-[#F4C2C2] bg-clip-text text-transparent font-['Cormorant_Garamond']">
                  {stat.value}
                </div>
                <div className="text-white/50 text-xs sm:text-sm mt-1 sm:mt-2">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section id="services" className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <span className="text-[#D4AF37] text-xs sm:text-sm font-medium tracking-widest uppercase">What We Offer</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-3 sm:mt-4 font-['Cormorant_Garamond']">
              Our Services
            </h2>
          </motion.div>

          <div className="space-y-16 sm:space-y-24">
            {services.map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid md:grid-cols-2 gap-8 sm:gap-12 items-center ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                  <Link to={`/services?category=${service.id}`} className="block">
                    <div className="relative group cursor-pointer">
                      <div 
                        className="absolute -inset-2 sm:-inset-4 rounded-3xl opacity-20 blur-xl sm:blur-2xl group-hover:opacity-40 transition-opacity"
                        style={{ backgroundColor: service.color }}
                      />
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="relative rounded-2xl w-full aspect-[4/3] object-cover border border-white/10 group-hover:border-[#D4AF37]/50 transition-all group-hover:scale-[1.02]"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-2xl transition-all flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 text-white font-semibold text-lg transition-opacity">
                          View Services →
                        </span>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                  <Link to={`/services?category=${service.id}`} className="block group">
                    <div 
                      className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 transition-transform group-hover:scale-110"
                      style={{ backgroundColor: `${service.color}15` }}
                    >
                      <service.icon className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: service.color }} weight="duotone" />
                    </div>
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 font-['Cormorant_Garamond'] group-hover:text-[#D4AF37] transition-colors">
                      {service.title}
                    </h3>
                  </Link>
                  <p className="text-white/60 text-base sm:text-lg mb-6 sm:mb-8">{service.description}</p>
                  <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 sm:gap-3 text-white/80 text-sm sm:text-base">
                        <CheckCircle className="w-5 h-5 text-[#D4AF37]" weight="fill" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={`/services?category=${service.id}`}>
                    <Button 
                      className="rounded-full px-8 py-6 font-semibold hover:scale-105 transition-transform"
                      style={{ backgroundColor: service.color, color: '#050505' }}
                      data-testid={`book-${service.id}-btn`}
                    >
                      Book {service.title}
                      <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#F4C2C2] text-sm font-medium tracking-widest uppercase">Simple Process</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 font-['Cormorant_Garamond']">
              How It Works
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Choose Service', description: 'Select rides, laundry, courier, or errands' },
              { step: '02', title: 'Book Online', description: 'Pick your date, time, and any add-ons you need' },
              { step: '03', title: 'Relax', description: 'Our vetted professionals handle everything' }
            ].map((item, idx) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="bg-[#111111] rounded-2xl p-8 border border-white/10 h-full">
                  <div className="text-6xl font-bold text-[#D4AF37]/20 font-['Cormorant_Garamond'] mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 font-['Cormorant_Garamond']">
                    {item.title}
                  </h3>
                  <p className="text-white/60">{item.description}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] text-sm font-medium tracking-widest uppercase">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 font-['Cormorant_Garamond']">
              The NC Help Hub Difference
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-[#111111] rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-colors group"
              >
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                  <benefit.icon className="w-7 h-7 text-[#D4AF37]" weight="duotone" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-['Cormorant_Garamond']">
                  {benefit.title}
                </h3>
                <p className="text-white/50 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage / Check Zip */}
      <section id="coverage" className="py-24 px-6 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#F4C2C2] text-sm font-medium tracking-widest uppercase">Service Area</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6 font-['Cormorant_Garamond']">
              Check Your Coverage
            </h2>
            <p className="text-white/60 mb-8">
              We proudly serve Tarboro, NC and surrounding areas within 40 miles.
            </p>

            <div className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter your zip code"
                  value={zipCode}
                  onChange={(e) => checkZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="w-full px-6 py-4 bg-[#111111] border border-white/10 rounded-full text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37] text-center text-lg"
                  data-testid="zip-check-input"
                />
              </div>

              {zipResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-6 bg-[#111111] rounded-2xl border border-white/10"
                >
                  {zipResult.found ? (
                    zipResult.inArea ? (
                      <div>
                        <CheckCircle className="w-12 h-12 text-[#4ADE80] mx-auto mb-4" weight="fill" />
                        <p className="text-white font-semibold text-lg mb-2">
                          Great news! We serve {zipResult.city}
                        </p>
                        <p className="text-white/60">
                          {zipResult.isFree 
                            ? 'No mileage fee - you\'re in our core service area!' 
                            : `${zipResult.miles} miles from Tarboro • $${zipResult.extraCost.toFixed(2)} mileage fee`
                          }
                        </p>
                        <Link to="/register" className="inline-block mt-4">
                          <Button className="bg-[#D4AF37] hover:bg-[#E6C965] text-black rounded-full px-8" data-testid="zip-book-btn">
                            Book Now
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div>
                        <p className="text-[#F87171] font-semibold">Outside Service Area</p>
                        <p className="text-white/60 mt-2">{zipResult.city} is {zipResult.miles} miles away, beyond our 40-mile range.</p>
                      </div>
                    )
                  ) : (
                    <p className="text-white/60">{zipResult.message}</p>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-[#D4AF37]/20 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Cormorant_Garamond']">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/60 mb-10">
              Your local, reliable help for rides, deliveries, errands, and laundry in Tarboro & surrounding areas.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register">
                <Button className="bg-[#D4AF37] hover:bg-[#E6C965] text-black text-lg px-10 py-6 rounded-full font-semibold" data-testid="cta-book-btn">
                  Book Your First Service
                  <ArrowRight className="ml-2 w-5 h-5" weight="bold" />
                </Button>
              </Link>
              <Link to="/join">
                <Button variant="outline" className="text-lg px-10 py-6 rounded-full border-2 border-[#F4C2C2]/50 text-[#F4C2C2] hover:bg-[#F4C2C2]/10" data-testid="cta-careers-btn">
                  Join Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] rounded-xl flex items-center justify-center">
                  <span className="text-black font-bold text-lg">H</span>
                </div>
                <span className="text-xl font-bold text-white font-['Cormorant_Garamond']">NC Help Hub</span>
              </Link>
              <p className="text-white/50 text-sm">
                Your hub for local rides and everyday services. Fast, reliable, professional.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/book-ride" className="text-[#A78BFA] hover:text-[#8B5CF6] text-sm transition-colors flex items-center gap-1.5">
                    <Car weight="bold" className="w-3.5 h-3.5" />
                    Rides
                    <span className="ml-1 px-1.5 py-0.5 bg-[#8B5CF6]/30 text-[#A78BFA] text-[9px] font-bold rounded">PRIMARY</span>
                  </Link>
                </li>
                <li><Link to="/book-courier" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Courier & Delivery</Link></li>
                <li><Link to="/book-errand" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Errands & Tasks</Link></li>
                <li><Link to="/book-laundry" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Laundry Pickup</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/track" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Track Order</Link></li>
                <li><Link to="/join" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Work With Us</Link></li>
                <li><Link to="/careers" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Open Positions</Link></li>
                <li><Link to="/login" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Sign In</Link></li>
                <li><Link to="/register" className="text-white/50 hover:text-[#D4AF37] text-sm transition-colors">Get Started</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Connect</h4>
              <div className="flex gap-3 mb-4 flex-wrap">
                {SOCIAL_LINKS.map((social) => (
                  <a 
                    key={social.name}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center hover:bg-[#D4AF37]/20 transition-colors group"
                    data-testid={`footer-${social.name.toLowerCase()}`}
                    title={social.name}
                  >
                    <social.icon className="w-5 h-5 text-white/70 group-hover:text-white" weight="fill" />
                  </a>
                ))}
              </div>
              
              {/* Hours of Operation */}
              <div className="mt-4 p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-[#D4AF37] font-semibold mb-2">Hours of Operation</p>
                <p className="text-xs text-white/60">Mon - Fri: 7am - 9pm</p>
                <p className="text-xs text-white/60">Sat - Sun: 8am - 6pm</p>
                <p className="text-[10px] text-white/40 mt-1">Online booking available 24/7</p>
              </div>
              
              {/* Phone Number */}
              <a 
                href="tel:+12529302698" 
                className="flex items-center gap-2 mt-4 p-3 bg-[#D4AF37]/10 rounded-lg hover:bg-[#D4AF37]/20 transition-colors group"
                onClick={() => {
                  trackEvent(Events.PHONE_NUMBER_CLICK, EventCategories.CTA, { location: 'footer' });
                  trackConversion(ConversionTypes.PHONE_CALL, 'general');
                }}
              >
                <Phone className="w-5 h-5 text-[#D4AF37]" weight="fill" />
                <div>
                  <p className="text-[#D4AF37] font-semibold">(252) 930-2698</p>
                  <p className="text-[10px] text-white/40">Call during business hours</p>
                </div>
              </a>
              
              <a href="mailto:nchelphub@outlook.com" className="text-[#D4AF37] text-sm hover:text-[#E6C965] transition-colors block mb-2 mt-4">
                nchelphub@outlook.com
              </a>
              <button 
                onClick={() => setShowChat(true)}
                className="text-[#F4C2C2] text-sm hover:text-[#F4C2C2]/80 transition-colors underline"
              >
                Contact Form
              </button>
              <p className="text-white/50 text-sm mt-3">
                Tarboro, NC & surrounding areas
              </p>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} NC Help Hub. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      <ContactFormModal isOpen={showChat} onClose={() => setShowChat(false)} />
      
      {/* PWA Install Banner */}
      <PWAInstallBanner />
    </div>
  );
};

export default Landing;
